import type { AttemptListOptions } from '@dcs/api';
import type { AttemptPage } from '@dcs/contracts';
import { useQueries, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import type { LatestAttempt } from './continue';

/** The one call the home page makes; tests pass a double. */
export interface LatestAttemptApi {
  listAttempts(options?: AttemptListOptions): Promise<AttemptPage>;
}

/** Loads the API client on first use: `@dcs/api` stays out of the home chunk. */
export type LoadLatestAttemptApi = () => Promise<LatestAttemptApi>;

let shared: Promise<LatestAttemptApi> | null = null;

export const loadLatestAttemptApi: LoadLatestAttemptApi = () => {
  shared ??= import('@dcs/api').then(({ createApiClient }) => createApiClient());
  // A failed chunk load (offline on a first visit) is tried again next time.
  shared.catch(() => {
    shared = null;
  });
  return shared;
};

/** Under `['attempts', userId]`, so a saved result (`invalidateQueries(['attempts'])`) refreshes it. */
export const latestAttemptKey = (userId: string) => ['attempts', userId, 'latest'] as const;

/** Newest saved theory attempt of the user (any class), `null` when there is none. */
export async function fetchLatestAttempt(api: LatestAttemptApi): Promise<LatestAttempt | null> {
  const page = await api.listAttempts({ kind: 'theory', limit: 1 });
  const item = page.items[0];
  if (item === undefined) return null;
  const finishedAt = Date.parse(item.finishedAt);
  return {
    licenseClass: item.licenseClass,
    passed: item.passed,
    score: item.score,
    maxScore: item.maxScore,
    finishedAt: Number.isFinite(finishedAt) ? finishedAt : 0,
  };
}

/** What the card reads: `null` for a guest (no query exists at all). */
export type LatestAttemptQuery = UseQueryResult<LatestAttempt | null> | null;

/**
 * The latest attempt of the signed-in user (design A-6). A guest gets `null` and no query is even
 * created (`useQueries` with no entry), so the cache holds nothing of a signed-out session.
 * `isPaused` (offline, TanStack's network mode) and `isError` are shown as one line by the card;
 * the rest of the page does not depend on it.
 */
export function useLatestAttempt(
  userId: string | null,
  loadApi: LoadLatestAttemptApi,
): LatestAttemptQuery {
  const queries: UseQueryOptions<LatestAttempt | null>[] =
    userId === null
      ? []
      : [
          {
            queryKey: latestAttemptKey(userId),
            staleTime: 30_000,
            retry: 1,
            queryFn: async () => fetchLatestAttempt(await loadApi()),
          },
        ];
  const results = useQueries({ queries });
  return results[0] ?? null;
}
